// hooks/usePreviousNodes.ts
import { useMemo } from "react";
import type { Node, Edge } from "reactflow";

interface PreviousNodesResult {
  previousNodes: Node[];
  previousContextFields: any[];
}

/**
 * Hook to get all previous nodes and their context fields
 * Traverses backwards through edges to build a linked list
 */
export const usePreviousNodes = (
  currentNodeId: string | null,
  nodes: Node[],
  edges: Edge[]
): PreviousNodesResult => {
  return useMemo(() => {
    if (!currentNodeId) {
      return { previousNodes: [], previousContextFields: [] };
    }

    // Step 1: Find all node IDs that come before current node
    const previousNodeIds = findPreviousNodeIds(currentNodeId, edges);

    // Step 2: Get actual node objects
    const previousNodes = nodes.filter((node) =>
      previousNodeIds.includes(node.id)
    );

    // Step 3: Collect all context fields from previous nodes
    const previousContextFields = collectContextFields(previousNodes);

    return {
      previousNodes,
      previousContextFields,
    };
  }, [currentNodeId, nodes, edges]);
};

/**
 * Recursively find all node IDs that come before the current node
 * Uses BFS (Breadth-First Search) to traverse backwards through edges
 */
function findPreviousNodeIds(nodeId: string, edges: Edge[]): string[] {
  const visited = new Set<string>();
  const queue: string[] = [nodeId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    // Find all edges that END at the current node (incoming edges)
    const incomingEdges = edges.filter((edge) => edge.target === currentId);

    for (const edge of incomingEdges) {
      if (!visited.has(edge.source)) {
        visited.add(edge.source);
        queue.push(edge.source); // Add source node to queue for further traversal
      }
    }
  }

  return Array.from(visited);
}

/**
 * Collect and merge context fields from all previous nodes
 * Each field is prefixed with node info for clarity
 */
function collectContextFields(nodes: Node[]): any[] {
  const allContextFields: any[] = [];

  nodes.forEach((node) => {
    const nodeData = node.data;
    const contextFields = nodeData?.contextFields || [];

    contextFields.forEach((field: any) => {
      if (!field?.hideFromUI) {
        allContextFields.push({
          ...field,
          key: `${field.key}`,
          nodeId: node.id,
          nodeLabel: nodeData?.label || "Unknown Node",
          nodeIcon: nodeData?.icon || "📦",
          nodeColor: nodeData?.color || "#999",
          originalKey: field.key,
          hideFromUI: field?.hideFromUI || false,
        });
      }
    });
  });

  return allContextFields;
}

export default usePreviousNodes;
